import React, { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Plane,
    Radar,
    Crosshair,
    Cpu,
    Satellite,
    Eye,
    ShieldAlert,
    Shield,
    FileSignature,
    Handshake,
    Network,
    Lock,
    ScrollText,
    UploadCloud,
    Send,
    Loader2,
    X,
    Phone,
    Mail,
    MapPin,
    ChevronRight,
    Layers,
    Antenna,
    Bot,
    Wrench,
    Boxes,
    GraduationCap,
    Building2,
} from "lucide-react";
import { toast } from "sonner";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline, SectionHeading } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FOCUS_AREAS = [
    {
        icon: Plane,
        title: "UAVs & Drone Systems",
        desc: "UAV, tactical drone, surveillance drone, interceptor drone, FPV drone, logistics drone, and dual-use drone opportunities.",
    },
    {
        icon: ShieldAlert,
        title: "Counter-UAS & Homeland Security",
        desc: "Counter-drone systems, detection platforms, surveillance solutions, perimeter security, and homeland security technologies.",
    },
    {
        icon: Crosshair,
        title: "Ammunition & Defence Supply Chains",
        desc: "Structured engagement with ammunition suppliers, components, supply-chain mapping, and defence manufacturing — subject to applicable laws, licenses and end-use requirements.",
    },
    {
        icon: Plane,
        title: "Aerospace Components & Systems",
        desc: "Aerospace suppliers, aircraft components, avionics, unmanned systems, maintenance-linked opportunities and technology partnerships.",
    },
    {
        icon: Radar,
        title: "Surveillance, ISR & Security Platforms",
        desc: "ISR, radar, electro-optical systems, border security, and institutional security solutions.",
    },
    {
        icon: Cpu,
        title: "Dual-Use & Emerging Defence Technologies",
        desc: "AI, robotics, cybersecurity, satellite & secure communication, sensor networks and battlefield-support technologies.",
    },
];

const ROLE_POINTS = [
    "Strategic representation",
    "Market access advisory",
    "Supplier identification",
    "Buyer and institutional engagement support",
    "Procurement documentation support",
    "Capability presentation structuring",
    "Product deck preparation",
    "LOI / SCO / FCO / CIS / KYC coordination",
    "NDA / NCNDA / IMFPA support",
    "Mandate and authority validation",
    "Technical specification coordination",
    "Commercial negotiation support",
    "Compliance-sensitive transaction structuring",
    "End-user and end-use documentation support",
    "Meeting coordination with serious counterparties",
];

const PROCESS = [
    { n: "01", t: "Requirement Assessment", d: "Review of platform type, quantity, end-user profile, delivery timeline and documentation readiness." },
    { n: "02", t: "Counterparty Verification", d: "Assessment of the buyer, supplier, mandate holder, OEM or representative for commercial credibility and documentation strength." },
    { n: "03", t: "Technical & Commercial Alignment", d: "Aligning technical specifications, pricing, delivery schedules, inspection requirements and commercial procedures." },
    { n: "04", t: "Documentation Structuring", d: "Coordination of NDA, NCNDA, LOI, SCO, capability deck, company profile, product datasheets, KYC and authorisation letters." },
    { n: "05", t: "Strategic Introduction", d: "Structured introduction and communication between serious, verified counterparties." },
    { n: "06", t: "Execution Coordination", d: "Transaction follow-up, meeting coordination, procedural alignment and commercial closure support." },
];

const COMPLIANCE_POINTS = [
    "End-user clarity",
    "End-use declaration where applicable",
    "Export control awareness",
    "Import licensing awareness",
    "SCOMET sensitivity where relevant",
    "Sanctions and restricted-party awareness",
    "Mandate and authority validation",
    "Defence procurement documentation",
    "Confidentiality and non-circumvention protection",
    "Written communication trail",
    "Proper technical and commercial documentation",
];

const DOCUMENTS = [
    "Company Profile",
    "Product Catalogue",
    "Technical Datasheet",
    "Capability Statement",
    "Authorization Letter",
    "Mandate Letter",
    "NDA",
    "NCNDA",
    "LOI",
    "SCO",
    "FCO",
    "CIS / KYC",
    "End-User Certificate (where applicable)",
    "End-Use Declaration (where applicable)",
    "Export License (where applicable)",
    "Import License (where applicable)",
    "Test Reports / Certifications",
    "Past Performance / Order History",
];

const CATEGORIES = [
    { icon: Plane, label: "UAVs & Drone Systems" },
    { icon: ShieldAlert, label: "Counter-UAS Systems" },
    { icon: Radar, label: "Surveillance & ISR Platforms" },
    { icon: Crosshair, label: "Ammunition & Components" },
    { icon: Plane, label: "Aerospace Components" },
    { icon: Eye, label: "Border Security Systems" },
    { icon: Antenna, label: "Tactical Communication" },
    { icon: Lock, label: "Secure Communication Systems" },
    { icon: Shield, label: "Cybersecurity" },
    { icon: Satellite, label: "Satellite Communication" },
    { icon: Bot, label: "AI & Robotics" },
    { icon: Layers, label: "Sensors & Detection Systems" },
    { icon: Boxes, label: "Logistics & Support Equipment" },
    { icon: GraduationCap, label: "Training & Simulation" },
    { icon: Wrench, label: "Maintenance & Support Services" },
    { icon: Cpu, label: "Dual-Use Technologies" },
];

const WHO = [
    "Defence OEMs seeking market access",
    "International suppliers seeking India or overseas representation",
    "Institutional buyers seeking verified suppliers",
    "Drone and counter-drone companies",
    "Aerospace technology companies",
    "Defence component manufacturers",
    "Strategic representatives",
    "Mandate holders",
    "Government-linked procurement channels",
    "Investors evaluating defence and aerospace opportunities",
    "Technology companies entering defence applications",
];

const COUNTERPARTY_OPTIONS = [
    "Buyer", "Seller", "OEM", "Manufacturer", "Supplier",
    "Mandate Holder", "Strategic Representative", "Government-Linked Entity",
    "Investor", "Consultant", "Other",
];

const DEFENCE_CATEGORY_OPTIONS = [
    "UAV / Drone Systems",
    "Counter-UAS",
    "Ammunition / Components",
    "Aerospace Systems",
    "Surveillance / ISR",
    "Secure Communication",
    "Cybersecurity",
    "Border Security",
    "AI / Robotics",
    "Satellite Communication",
    "Dual-Use Technology",
    "Other",
];

const DOCUMENTS_AVAILABLE_OPTIONS = [
    "Product Catalogue",
    "Technical Datasheet",
    "Company Profile",
    "Authorization Letter",
    "Mandate Letter",
    "LOI",
    "SCO",
    "NDA",
    "NCNDA",
    "CIS / KYC",
    "End-User Certificate",
    "End-Use Declaration",
    "Export License",
    "Import License",
    "Not Available Yet",
    "Other",
];

const TIMELINE_OPTIONS = [
    "Immediate (< 1 month)",
    "Short-term (1–3 months)",
    "Medium-term (3–6 months)",
    "Long-term (6+ months)",
    "Exploratory",
];

const initialForm = {
    name: "",
    company: "",
    designation: "",
    country: "",
    email: "",
    phone: "",
    counterparty_type: COUNTERPARTY_OPTIONS[0],
    defence_category: DEFENCE_CATEGORY_OPTIONS[0],
    product_details: "",
    scope: "",
    target_country: "",
    timeline: TIMELINE_OPTIONS[0],
    documents_available: DOCUMENTS_AVAILABLE_OPTIONS[0],
    compliance_status: "",
    message: "",
};

const ALLOWED_EXTS = [".pdf", ".doc", ".docx", ".png", ".jpg", ".jpeg"];
const MAX_BYTES = 10 * 1024 * 1024;

const SectorsDefence = () => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) {
            toast.error("Unsupported file type. Allowed: PDF, DOC, DOCX, PNG, JPG.");
            e.target.value = "";
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error("File exceeds 10 MB limit.");
            e.target.value = "";
            return;
        }
        setFile(f);
    };

    const removeFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.product_details.trim()) {
            toast.error("Name, email and product / requirement details are required.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
            if (file) fd.append("file", file);
            await axios.post(`${API}/defence-enquiry`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Defence enquiry received. A principal will respond within two working days.");
            setForm(initialForm);
            removeFile();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Unable to submit at this time. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-enter">
            <SEO
                title="Defence & Aerospace Advisory | Defence Procurement & Strategic Representation"
                description="Indravax Strategic Advisory LLP supports defence, aerospace, homeland security, UAV, counter-UAS, ammunition supply-chain, surveillance, and dual-use technology opportunities through strategic advisory, representation, documentation, and cross-border transaction support."
                path="/sectors/defence-aerospace"
                keywords="defence advisory firm India, defence procurement support India, aerospace advisory India, defence representation services, UAV advisory India, drone procurement advisory, counter UAS advisory, defence supplier facilitation, defence market entry India, homeland security advisory, ammunition supply chain advisory, dual-use technology advisory, defence transaction support, defence documentation support, defence OEM representation, defence strategic advisory"
            />

            {/* Breadcrumb */}
            <div className="border-b border-[#C9A14A]/10" data-testid="defence-breadcrumb">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center gap-2 text-[0.65rem] tracking-[0.22em] uppercase text-slate-500">
                    <Link to="/" className="hover:text-[#C9A14A] transition-colors">Home</Link>
                    <ChevronRight size={12} className="text-[#C9A14A]/40" />
                    <Link to="/sectors" className="hover:text-[#C9A14A] transition-colors">Sectors</Link>
                    <ChevronRight size={12} className="text-[#C9A14A]/40" />
                    <span className="text-[#C9A14A]">Defence & Aerospace</span>
                </div>
            </div>

            {/* HERO */}
            <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="defence-hero">
                <img
                    src="https://images.unsplash.com/photo-1532375810709-75b1da00537c?auto=format&fit=crop&w=2400&q=80"
                    alt="Aerospace and defence command centre"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#040B1A]/88" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#040B1A] via-[#040B1A]/85 to-[#040B1A]/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040B1A]" />
                <GlobeLines className="opacity-[0.12]" />
                <div className="grain-overlay" />

                {/* Blueprint-style grid */}
                <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{
                        backgroundImage:
                            "linear-gradient(rgba(201,161,74,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(201,161,74,0.4) 1px, transparent 1px)",
                        backgroundSize: "80px 80px",
                    }}
                />

                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="inline-flex items-center gap-3 mb-10">
                            <Shield size={16} className="text-[#C9A14A]" />
                            <span className="overline">Defence & Aerospace Vertical</span>
                        </div>
                        <h1 className="font-serif text-4xl md:text-6xl lg:text-[4.5rem] text-white leading-[1.05] tracking-tight">
                            Defence & Aerospace Advisory for{" "}
                            <span className="italic font-light text-[#C9A14A]">Strategic, Institutional, and Cross-Border Engagements.</span>
                        </h1>
                        <p className="mt-10 text-base md:text-lg text-slate-300 font-light leading-[1.85] max-w-3xl">
                            Indravax Strategic Advisory LLP supports defence, aerospace, homeland security and
                            dual-use technology opportunities through strategic representation, market access,
                            supplier-buyer coordination, documentation support, procurement advisory and structured
                            transaction facilitation.
                        </p>
                        <p className="mt-5 text-base md:text-lg text-slate-300 font-light leading-[1.85] max-w-3xl">
                            We work with serious counterparties where credibility, compliance, end-use clarity,
                            mandate validation, technical documentation and institutional engagement are essential.
                        </p>
                        <p className="mt-3 text-[0.7rem] tracking-[0.3em] uppercase text-[#C9A14A]">
                            Credibility · Compliance · Strategic Access
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-4">
                            <a href="#submit" className="btn-primary group" data-testid="defence-hero-submit">
                                Submit Defence Requirement
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </a>
                            <Link to="/contact" className="btn-secondary" data-testid="defence-hero-discussion">
                                Request Defence Capability Discussion
                            </Link>
                            <Link to="/contact" className="btn-secondary" data-testid="defence-hero-partner">
                                Partner With Indravax
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* OVERVIEW */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="defence-overview">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-4">
                        <Overline>Overview</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Built for <em className="text-[#C9A14A] not-italic">serious defence engagement.</em>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-8 space-y-6 text-lg text-slate-300 font-light leading-[1.9]">
                        <p>
                            Indravax supports defence and aerospace opportunities that require structured engagement,
                            regulatory awareness, institutional credibility, technical documentation and disciplined
                            execution.
                        </p>
                        <p>
                            The Defence & Aerospace vertical focuses on advisory and facilitation support across
                            <span className="text-[#C9A14A]"> products, technologies, platforms, suppliers, and
                            procurement-linked opportunities.</span>
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* FOCUS AREAS */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="defence-focus">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Focus Areas"
                        title="Six lines of engagement."
                        description="Each focus area is supported through structured advisory, compliance-led documentation and verified counterparty access."
                        testId="defence-focus-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FOCUS_AREAS.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <FadeUp key={f.title} delay={i * 0.06}>
                                    <div className="institutional-card h-full" data-testid={`defence-focus-${i}`}>
                                        <div className="flex items-start justify-between">
                                            <Icon size={28} strokeWidth={1.1} className="text-[#C9A14A]" />
                                            <span className="text-[0.65rem] tracking-[0.3em] text-slate-500 uppercase">0{i + 1}</span>
                                        </div>
                                        <h3 className="font-serif text-xl text-white mt-6 leading-tight">{f.title}</h3>
                                        <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{f.desc}</p>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* OUR ROLE */}
            <section className="py-24 md:py-32" data-testid="defence-role">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <FadeUp className="lg:col-span-5">
                        <Overline>Our Role</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Our role in defence & aerospace engagements.
                        </h2>
                        <p className="text-base text-slate-400 mt-6 font-light leading-relaxed">
                            Indravax may support defence and aerospace opportunities across representation,
                            structuring, documentation and coordination — strictly within applicable legal frameworks.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <ul className="border-t border-[#C9A14A]/20 grid grid-cols-1 sm:grid-cols-2" data-testid="defence-role-list">
                            {ROLE_POINTS.map((p, i) => (
                                <li key={p} className={`flex items-baseline gap-4 py-4 border-b border-[#C9A14A]/15 ${i % 2 === 0 ? "sm:border-r sm:pr-6" : "sm:pl-6"}`}>
                                    <span className="text-[0.65rem] tracking-[0.3em] text-[#C9A14A] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-sm text-slate-200 font-light leading-relaxed">{p}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                </div>
            </section>

            {/* PROCESS */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="defence-process">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Engagement Process"
                        title="A six-stage defence execution sequence."
                        description="Every defence mandate follows the same disciplined progression — from intake through structured closure."
                        testId="defence-process-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-[#C9A14A]/20">
                        {PROCESS.map((s, i) => (
                            <FadeUp
                                key={s.n}
                                delay={i * 0.06}
                                className={`p-8 md:p-10 border-b border-[#C9A14A]/20 ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""} ${(i + 1) % 2 !== 0 ? "md:border-r lg:border-r" : ""}`}
                            >
                                <div className="font-serif text-5xl text-[#C9A14A] italic">{s.n}</div>
                                <h3 className="font-serif text-2xl text-white mt-6 leading-tight">{s.t}</h3>
                                <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{s.d}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMPLIANCE */}
            <section className="py-24 md:py-32 relative overflow-hidden" data-testid="defence-compliance">
                <GlobeLines className="opacity-[0.06]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <FadeUp>
                        <div className="institutional-card h-full">
                            <Lock size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Compliance-Led Defence Engagement</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Discipline is the prerequisite, not the afterthought.
                            </h3>
                            <p className="mt-6 text-slate-300 font-light leading-[1.9]">
                                Defence and aerospace opportunities require disciplined documentation, regulatory
                                awareness, end-use clarity and credible counterparty validation. Indravax supports
                                only structured and lawful engagements, subject to applicable laws, licensing
                                requirements, export controls, import regulations, sanctions screening and
                                end-user documentation.
                            </p>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="institutional-card h-full">
                            <ShieldAlert size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Compliance Pillars</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Eleven non-negotiable disciplines.
                            </h3>
                            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6" data-testid="defence-compliance-list">
                                {COMPLIANCE_POINTS.map((c) => (
                                    <li key={c} className="flex items-start gap-3 pb-3 border-b border-[#C9A14A]/10">
                                        <span className="h-px w-3 bg-[#C9A14A] mt-3 shrink-0" />
                                        <span className="text-sm text-slate-300 font-light">{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* DOCUMENTS */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="defence-documents">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Documentation"
                        title="Documents commonly required in defence engagements."
                        description="Document requirements vary by product type, jurisdiction, buyer profile, end-use and applicable law."
                        testId="defence-documents-heading"
                    />
                    <FadeUp>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 border-t border-[#C9A14A]/20 pt-8">
                            {DOCUMENTS.map((d, i) => (
                                <div key={d} className="flex items-center gap-3 py-2.5 border-b border-[#C9A14A]/10" data-testid={`defence-doc-${i}`}>
                                    <FileSignature size={14} className="text-[#C9A14A] shrink-0" />
                                    <span className="text-sm text-slate-300 font-light">{d}</span>
                                </div>
                            ))}
                        </div>
                        <p className="mt-10 text-xs text-slate-500 font-light leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic max-w-3xl">
                            Document requirements vary depending on product type, jurisdiction, buyer profile,
                            end-use and applicable law. Indravax supports preparation, coordination and validation
                            of these documents within client mandates.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="py-24 md:py-32" data-testid="defence-categories">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Defence & Aerospace Categories"
                        title="Sixteen capability segments."
                        description="Coverage across air, ground, surveillance, communication, cyber, AI and dual-use platforms."
                        testId="defence-categories-heading"
                    />
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {CATEGORIES.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <FadeUp key={c.label} delay={i * 0.03}>
                                    <div
                                        className="border border-[#C9A14A]/20 bg-[#0A1F44]/30 p-5 h-full flex flex-col gap-3 hover:border-[#C9A14A]/60 hover:bg-[#0A1F44]/60 transition-all duration-500"
                                        data-testid={`defence-cat-${i}`}
                                    >
                                        <Icon size={22} strokeWidth={1.1} className="text-[#C9A14A]" />
                                        <span className="text-sm text-slate-200 font-light leading-tight">{c.label}</span>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHO SHOULD ENGAGE */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="defence-who">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <FadeUp className="lg:col-span-5">
                        <Overline>Who Should Engage With Us</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Built for serious institutional counterparties.
                        </h2>
                        <p className="text-base text-slate-400 mt-6 font-light leading-relaxed">
                            Indravax engages selectively with counterparties capable of structured documentation,
                            credible mandates and compliance discipline.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="lg:col-span-7">
                        <ul className="border-t border-[#C9A14A]/20" data-testid="defence-who-list">
                            {WHO.map((w, i) => (
                                <li key={w} className="flex items-baseline gap-5 py-4 border-b border-[#C9A14A]/15">
                                    <span className="text-[0.65rem] tracking-[0.3em] text-[#C9A14A] w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-base text-slate-200 font-light leading-relaxed">{w}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                </div>
            </section>

            {/* SUBMIT DEFENCE FORM */}
            <section id="submit" className="py-24 md:py-32 scroll-mt-28" data-testid="defence-submit">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        align="center"
                        overline="Initiate a Defence Engagement"
                        title="Submit a defence & aerospace requirement."
                        description="All defence enquiries are reviewed carefully for documentation readiness, lawful purpose, counterparty credibility and compliance suitability."
                        testId="defence-submit-heading"
                    />
                    <FadeUp>
                        <form
                            onSubmit={onSubmit}
                            encType="multipart/form-data"
                            className="institutional-card p-8 md:p-12"
                            data-testid="defence-form"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Full Name *</label>
                                    <input type="text" name="name" value={form.name} onChange={onChange} className="institutional-input" placeholder="Full name" required data-testid="defence-form-name" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Company Name</label>
                                    <input type="text" name="company" value={form.company} onChange={onChange} className="institutional-input" placeholder="Organisation" data-testid="defence-form-company" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Designation</label>
                                    <input type="text" name="designation" value={form.designation} onChange={onChange} className="institutional-input" placeholder="e.g. Director, Principal" data-testid="defence-form-designation" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Country</label>
                                    <input type="text" name="country" value={form.country} onChange={onChange} className="institutional-input" placeholder="Country of operation" data-testid="defence-form-country" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Email Address *</label>
                                    <input type="email" name="email" value={form.email} onChange={onChange} className="institutional-input" placeholder="you@org.com" required data-testid="defence-form-email" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">WhatsApp / Phone</label>
                                    <input type="tel" name="phone" value={form.phone} onChange={onChange} className="institutional-input" placeholder="+..." data-testid="defence-form-phone" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Type of Counterparty</label>
                                    <select name="counterparty_type" value={form.counterparty_type} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="defence-form-counterparty">
                                        {COUNTERPARTY_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Defence Category</label>
                                    <select name="defence_category" value={form.defence_category} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="defence-form-category">
                                        {DEFENCE_CATEGORY_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Quantity / Scope</label>
                                    <input type="text" name="scope" value={form.scope} onChange={onChange} className="institutional-input" placeholder="e.g. 500 units / USD 10M" data-testid="defence-form-scope" />
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Target Country / End User Type</label>
                                    <input type="text" name="target_country" value={form.target_country} onChange={onChange} className="institutional-input" placeholder="e.g. India MOD, UAE Armed Forces" data-testid="defence-form-target" />
                                </div>

                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Timeline</label>
                                    <select name="timeline" value={form.timeline} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="defence-form-timeline">
                                        {TIMELINE_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Documents Available</label>
                                    <select name="documents_available" value={form.documents_available} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="defence-form-docs">
                                        {DOCUMENTS_AVAILABLE_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Product / Requirement Details *</label>
                                    <textarea
                                        name="product_details"
                                        value={form.product_details}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="Describe the product, platform or requirement — technical specifications, intended use, mandate type and outcome sought."
                                        rows={5}
                                        required
                                        data-testid="defence-form-details"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Compliance / Licensing Status</label>
                                    <textarea
                                        name="compliance_status"
                                        value={form.compliance_status}
                                        onChange={onChange}
                                        className="institutional-input"
                                        placeholder="End-user clarity, export/import licence position, sanctions screening status, SCOMET sensitivity."
                                        rows={3}
                                        data-testid="defence-form-compliance"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Additional Message</label>
                                    <textarea name="message" value={form.message} onChange={onChange} className="institutional-input" placeholder="Timelines, jurisdictions, sensitivities." rows={3} data-testid="defence-form-message" />
                                </div>

                                {/* FILE UPLOAD */}
                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Upload Documents (optional)</label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        name="file"
                                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                                        onChange={onFileChange}
                                        className="hidden"
                                        data-testid="defence-form-file-input"
                                    />
                                    {!file ? (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full border border-dashed border-[#C9A14A]/40 hover:border-[#C9A14A] bg-[#040B1A]/50 transition-colors p-8 flex flex-col items-center justify-center gap-3 group"
                                            data-testid="defence-form-file-pick"
                                        >
                                            <UploadCloud size={28} className="text-[#C9A14A] group-hover:scale-110 transition-transform" />
                                            <span className="text-sm text-slate-300 font-light">
                                                Click to attach a document
                                            </span>
                                            <span className="text-[0.65rem] tracking-[0.25em] uppercase text-slate-500">
                                                PDF · DOC · DOCX · PNG · JPG · max 10 MB
                                            </span>
                                        </button>
                                    ) : (
                                        <div
                                            className="w-full border border-[#C9A14A]/40 bg-[#040B1A]/60 px-5 py-4 flex items-center justify-between gap-4"
                                            data-testid="defence-form-file-selected"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <UploadCloud size={20} className="text-[#C9A14A] shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="text-sm text-white truncate">{file.name}</div>
                                                    <div className="text-[0.7rem] text-slate-500">
                                                        {(file.size / 1024).toFixed(1)} KB
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={removeFile}
                                                className="text-slate-400 hover:text-[#C9A14A] transition-colors"
                                                data-testid="defence-form-file-remove"
                                                aria-label="Remove file"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                                <p className="text-xs text-slate-500 max-w-lg leading-relaxed font-light flex items-start gap-2">
                                    <ScrollText size={14} className="text-[#C9A14A] mt-0.5 shrink-0" />
                                    All defence-related enquiries are reviewed carefully for documentation readiness,
                                    lawful purpose, counterparty credibility and compliance suitability.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                    data-testid="defence-form-submit"
                                >
                                    {submitting ? (
                                        <><Loader2 size={14} className="animate-spin" /> Submitting</>
                                    ) : (
                                        <>Submit Defence Requirement <Send size={14} /></>
                                    )}
                                </button>
                            </div>
                        </form>
                    </FadeUp>
                </div>
            </section>

            {/* CONTACT + DISCLAIMER */}
            <section className="py-20 border-t border-[#C9A14A]/15 bg-[#07132E]/40" data-testid="defence-contact">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-10">
                    <div className="flex gap-3 text-sm text-slate-400">
                        <MapPin size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <span>Indravax Strategic Advisory LLP<br />New Delhi, India</span>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Phone size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="tel:+917814050036" className="hover:text-[#C9A14A] transition-colors">+91 78140 50036</a>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Mail size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="mailto:prateekgrewal@indravax.com" className="hover:text-[#C9A14A] transition-colors">prateekgrewal@indravax.com</a>
                    </div>
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-10 mt-12">
                    <p className="text-xs text-slate-500 font-light leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic">
                        Indravax Strategic Advisory LLP provides advisory and facilitation services across defence
                        and aerospace opportunities. The firm does not act as a principal buyer, seller, broker or
                        licensed defence trader unless specifically authorised under applicable laws, licences and
                        written agreements. All engagements are subject to applicable export controls, import
                        regulations, sanctions frameworks, end-use documentation and licensing requirements.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default SectorsDefence;
